// src/pages/ShopSettingsPage.jsx
import React, { useState } from 'react';
import { Save, Store, Truck, Gift, Languages, Boxes, Image as ImageIcon, Ruler } from 'lucide-react';
import { useShopSettings } from '../hooks/useShopSettings';
import ShopInfoTab from './shop-settings-tabs/ShopInfoTab';
import DeliveryTab from './shop-settings-tabs/DeliveryTab';
import PromotionTab from './shop-settings-tabs/PromotionTab';
import LanguageTab from './shop-settings-tabs/LanguageTab';
import UnitsTab from './shop-settings-tabs/UnitsTab';
import InventoryTab from './shop-settings-tabs/InventoryTab';
import BannersTab from './shop-settings-tabs/BannersTab';

const TABS = [
  { key: 'shop',      label: '店铺信息',   icon: Store },
  { key: 'delivery',  label: '配送与支付', icon: Truck },
  { key: 'promotion', label: '促销活动',   icon: Gift },
  { key: 'language',  label: '语言设置',   icon: Languages },
  { key: 'units',     label: '单位管理',   icon: Ruler },
  { key: 'inventory', label: '库存',       icon: Boxes },
  { key: 'banners',   label: '广告图管理', icon: ImageIcon },
];

const ShopSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('shop');

  const {
    language,
    settings, updateField, toggleField, toggleLanguage,
    banners, setBanners,
    loading, saving, error, success,
    logoPreview, handleLogoChange,
    handleSave,
  } = useShopSettings();

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-screen">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">商家设置</h2>
          <p className="text-gray-600 mt-1">管理店铺基本信息、Logo 和广告图</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" />
          <span>{saving ? '保存中...' : '保存设置'}</span>
        </button>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">{success}</div>}

      {/* 标签页导航——沿用数据看板渠道切换那套胶囊按钮样式 */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit flex-wrap">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'shop' && (
        <ShopInfoTab settings={settings} updateField={updateField}
          logoPreview={logoPreview} handleLogoChange={handleLogoChange} />
      )}

      {activeTab === 'delivery' && (
        <DeliveryTab settings={settings} updateField={updateField} toggleField={toggleField} />
      )}

      {activeTab === 'promotion' && (
        <PromotionTab settings={settings} toggleField={toggleField} />
      )}

      {activeTab === 'language' && (
        <LanguageTab settings={settings} toggleLanguage={toggleLanguage} />
      )}

      {activeTab === 'units' && (
        <UnitsTab language={language} />
      )}

      {activeTab === 'inventory' && (
        <InventoryTab settings={settings} toggleField={toggleField} />
      )}

      {activeTab === 'banners' && (
        <BannersTab banners={banners} setBanners={setBanners} />
      )}
    </div>
  );
};

export default ShopSettingsPage;